/**
 * SubscriptionBanners — Phase 3 (Drive-First Pivot).
 *
 * Reads the current subscription via useSubscription and renders the
 * appropriate banner at the top of the workspace, if any:
 *
 *   - Trial ending: T-30 (yellow), T-7 (orange), T-1 (red)
 *   - Trial ended: red
 *   - Past-due in grace: yellow with countdown
 *   - Blocked: red with renew CTA
 *   - Drive disconnected: red with reconnect CTA
 *
 * Drop into any layout you want banners on (e.g. PrivateLayout).
 * Renders nothing when nothing's wrong.
 */

import { Link } from 'react-router-dom'
import { useSubscription } from '../../hooks/useSubscription'
import { usePricingMode } from '../../contexts/PricingModeContext'

const PALETTE = {
  red:    { bg: 'bg-red-50',    border: 'border-red-300',    text: 'text-red-900',    btn: 'bg-red-600 hover:bg-red-700' },
  amber:  { bg: 'bg-amber-50',  border: 'border-amber-300',  text: 'text-amber-900',  btn: 'bg-amber-600 hover:bg-amber-700' },
  yellow: { bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-900', btn: 'bg-yellow-600 hover:bg-yellow-700' },
  blue:   { bg: 'bg-blue-50',   border: 'border-blue-300',   text: 'text-blue-900',   btn: 'bg-blue-600 hover:bg-blue-700' },
}

function Banner({ tone = 'amber', message, cta, ctaTo }) {
  const c = PALETTE[tone] || PALETTE.amber
  return (
    <div className={`rounded-lg border ${c.bg} ${c.border} ${c.text} p-3 mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2`} role="status">
      <div className="text-sm font-medium">{message}</div>
      {cta && ctaTo && (
        <Link
          to={ctaTo}
          className={`inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded ${c.btn} text-white flex-shrink-0`}
        >
          {cta}
        </Link>
      )}
    </div>
  )
}

export default function SubscriptionBanners() {
  const { isDriveFirst } = usePricingMode()
  const { subscription, loading } = useSubscription()

  if (loading) return null
  if (!isDriveFirst) return null
  if (!subscription) return null
  if (subscription.is_grandfathered) return null

  const banners = []

  // Trial countdown
  if (subscription.is_in_trial) {
    const d = subscription.days_until_trial_end
    if (d != null && d <= 1) {
      banners.push({
        tone: 'red',
        message: 'Last day of your free trial — subscribe by midnight to keep uploading.',
        cta: 'Subscribe now',
        ctaTo: '/profile-settings?tab=subscription',
      })
    } else if (d != null && d <= 7) {
      banners.push({
        tone: 'amber',
        message: `Only ${d} days left in your free trial. Subscribe for $${subscription.current_price_usd}/year.`,
        cta: 'Subscribe now',
        ctaTo: '/profile-settings?tab=subscription',
      })
    } else if (d != null && d <= 30) {
      banners.push({
        tone: 'yellow',
        message: `${d} days left in your free trial.`,
        cta: 'Manage subscription',
        ctaTo: '/profile-settings?tab=subscription',
      })
    }
  }

  // Trial expired
  if (subscription.status === 'expired' && !subscription.is_grandfathered) {
    banners.push({
      tone: 'red',
      message: 'Your free trial has ended. Uploads are paused.',
      cta: 'Subscribe',
      ctaTo: '/profile-settings?tab=subscription',
    })
  }

  // Past due in grace
  if (subscription.is_in_grace) {
    const endsAt = subscription.grace_period_ends_at ? new Date(subscription.grace_period_ends_at) : null
    const daysLeft = endsAt ? Math.max(0, Math.ceil((endsAt - Date.now()) / (1000 * 60 * 60 * 24))) : null
    banners.push({
      tone: 'amber',
      message: daysLeft != null
        ? `Payment failed. ${daysLeft} days to update your card before uploads pause.`
        : 'Payment failed. Update your card to keep uploading.',
      cta: 'Update payment',
      ctaTo: '/profile-settings?tab=subscription',
    })
  }

  // Blocked
  if (subscription.is_blocked) {
    banners.push({
      tone: 'red',
      message: 'Your subscription needs renewal. Uploads are paused.',
      cta: 'Renew',
      ctaTo: '/profile-settings?tab=subscription',
    })
  }

  // Cancellation pending (cancel_at_period_end)
  if (subscription.status === 'active' && subscription.cancelled_at) {
    banners.push({
      tone: 'blue',
      message: 'Subscription cancelled. You keep access until your current period ends.',
      cta: 'Reactivate',
      ctaTo: '/profile-settings?tab=subscription',
    })
  }

  if (banners.length === 0) return null

  return (
    <div className="px-4 pt-3">
      {banners.map((b, i) => <Banner key={i} {...b} />)}
    </div>
  )
}
